import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import FastScanInput from "@/components/FastScanInput";
import CameraCapture from "@/components/CameraCapture";
import { StatusBadge, OccurrenceBadges } from "@/components/StatusUI";
import { fmtDate, DIVERG_TIPOS } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, PackageCheck, Check, AlertTriangle, Truck } from "lucide-react";
import { toast } from "sonner";
import { LotesPedido } from "@/components/LotesPedido";

export default function Recebimento() {
  const nav = useNavigate();
  const [pedidos, setPedidos] = useState(null);
  const [sel, setSel] = useState(null);
  const [flash, setFlash] = useState("");
  const [alerta, setAlerta] = useState("");
  const [units, setUnits] = useState({});
  const [busy, setBusy] = useState(false);
  const [divModal, setDivModal] = useState(false);
  const [div, setDiv] = useState({ item_id: "", tipo: "FALTANTE", quantidade: 1, motivo: "", foto_path: null, destino: "" });

  const loadPed = async (id) => { const { data } = await api.get(`/pedidos/${id}`); setSel(data); };
  const load = useCallback(async () => {
    const { data } = await api.get("/pedidos?status=EM_TRANSITO");
    setPedidos(data);
    const u = await api.get("/units");
    setUnits(Object.fromEntries(u.data.map((x) => [x.id, x.nome])));
  }, []);
  useEffect(() => { load(); }, [load]);

  const flashScreen = (ok) => { setFlash(ok ? "scan-flash-ok" : "scan-flash-err"); setTimeout(() => setFlash(""), 500); };

  const bipar = async ({ codigo, quantidade, is_master }) => {
    try {
      const { data } = await api.post(`/pedidos/${sel.id}/recebimento/bipar`, { codigo, quantidade, is_master });
      flashScreen(true);
      toast.success(`${data.nome}: ${data.feito}/${data.alvo}${data.completo ? " — COMPLETO ✓" : ` · faltam ${data.restante}`}`);
      await loadPed(sel.id);
    } catch (e) {
      flashScreen(false);
      const msg = e.response?.data?.detail;
      const txt = typeof msg === "string" ? msg : "Bipagem inválida";
      if (txt.includes("QUANTIDADE JÁ ATINGIDA")) { setAlerta(txt); setTimeout(() => setAlerta(""), 2600); }
      toast.error(txt);
    }
  };

  const confirmar = async () => {
    setBusy(true);
    try {
      await api.post(`/pedidos/${sel.id}/recebimento/confirmar`);
      toast.success("Recebimento confirmado — estoque creditado");
      setSel(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  const openDiv = (itemId) => { setDiv({ item_id: itemId || sel.itens[0]?.id, tipo: "FALTANTE", quantidade: 1, motivo: "", foto_path: null, destino: "" }); setDivModal(true); };
  const setD = (k, v) => setDiv((d) => ({ ...d, [k]: v }));

  const salvarDiv = async () => {
    if (div.tipo === "AVARIA" && !div.foto_path) return toast.error("Avaria exige foto capturada ao vivo");
    if (div.tipo === "AVARIA" && !div.destino) return toast.error("Escolha o destino da quantidade avariada");
    setBusy(true);
    try {
      await api.post(`/pedidos/${sel.id}/divergencia`, {
        item_id: div.item_id, tipo: div.tipo, quantidade: Number(div.quantidade) || 0,
        motivo: div.motivo, foto_path: div.foto_path, destino: div.tipo === "AVARIA" ? div.destino : undefined,
      });
      toast.success(div.tipo === "AVARIA" ? "Avaria registrada e destino aplicado" : "Ocorrência registrada");
      setDivModal(false);
      await loadPed(sel.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao registrar"); }
    finally { setBusy(false); }
  };

  const activeOcs = sel ? [...new Set((sel.ocorrencias || []).filter((o) => o.status === "ABERTA").map((o) => o.tipo))] : [];

  return (
    <div className={`space-y-5 ${flash} rounded-lg`} data-testid="recebimento-page">
      <div>
        <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Chegada na Unidade</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase">Recebimento</h1>
      </div>

      {!pedidos ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : !sel ? (
        pedidos.length === 0 ? (
          <Card className="p-10 text-center text-muted-foreground">Nenhum pedido em trânsito para receber.</Card>
        ) : (
          <div className="grid gap-3">
            <div className="text-sm text-muted-foreground uppercase tracking-wide">Pedidos a Receber</div>
            {pedidos.map((p) => (
              <Card key={p.id} className="bg-card/70 p-4 flex items-center justify-between cursor-pointer hover:border-primary/50" onClick={() => loadPed(p.id)} data-testid={`receber-${p.numero}`}>
                <div>
                  <div className="font-mono font-bold text-lg">{p.numero}</div>
                  <div className="text-xs text-muted-foreground">{p.itens.length} itens · {fmtDate(p.created_at)}</div>
                </div>
                <StatusBadge status={p.status} />
              </Card>
            ))}
          </div>
        )
      ) : (
        <div className="space-y-4">
          <Button variant="outline" size="sm" onClick={() => setSel(null)}>← Voltar</Button>
          <Card className="bg-card/70 p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 font-display text-xl font-bold uppercase"><PackageCheck className="w-5 h-5 text-primary" /> {sel.numero}</div>
            <div className="inline-flex items-center gap-2 rounded-md border border-primary/50 bg-primary/10 px-3 py-1.5" data-testid="rec-destino">
              <Truck className="w-5 h-5 text-primary shrink-0" />
              <div>
                <div className="text-[9px] uppercase text-muted-foreground leading-none tracking-wide">Unidade destino</div>
                <div className="font-display text-lg font-extrabold uppercase leading-tight">{units[sel.unit_id] || "—"}</div>
              </div>
            </div>
            <Button variant="outline" className="text-rose-300 border-rose-500/40" onClick={() => openDiv(null)} data-testid="btn-registrar-divergencia">
              <AlertTriangle className="w-4 h-4" /> Registrar Divergência / Avaria
            </Button>
          </Card>

          {activeOcs.length > 0 && <OccurrenceBadges tipos={activeOcs} />}

          {alerta && <div className="rounded-md border-2 border-red-500 bg-red-950/70 text-red-200 font-bold uppercase text-center py-2.5 animate-pulse" data-testid="rec-alerta">⚠ {alerta}</div>}

          <FastScanInput label="Bipar Recebimento" onScan={bipar} testid="rec-scan" />

          <LotesPedido pedidoId={sel.id} />

          <Card className="bg-card/70 p-4">
            <div className="space-y-2">
              {sel.itens.map((it) => {
                const enviado = it.qtd_expedida;
                const recebido = it.qtd_recebida;
                const faltante = Math.max(0, enviado - recebido);
                const ok = enviado > 0 && recebido >= enviado;
                return (
                  <div key={it.id} className={`rounded-md border p-3 ${ok ? "border-emerald-500/60 bg-emerald-950/30" : "border-border"}`} data-testid={`rec-item-${it.sku}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0"><div className="text-sm font-medium truncate">{it.nome}</div><div className="text-[11px] font-mono text-muted-foreground">{it.sku}</div></div>
                      <div className="flex items-center gap-2">
                        {ok
                          ? <span className="text-[11px] font-bold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-500/50 px-2 py-1 rounded-sm font-mono flex items-center gap-1" data-testid={`rec-completo-${it.sku}`}><Check className="w-3.5 h-3.5" /> Completo</span>
                          : <span className="font-mono font-extrabold text-2xl text-amber-300">{recebido}<span className="text-muted-foreground text-lg">/{enviado}</span></span>}
                        <Button variant="ghost" size="sm" className="text-rose-300 h-8" onClick={() => openDiv(it.id)} data-testid={`div-item-${it.sku}`}>
                          <AlertTriangle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-2.5 text-center">
                      <div className="bg-secondary/40 rounded p-1.5"><div className="text-[9px] uppercase text-muted-foreground">Enviado</div><div className="font-mono font-bold">{enviado}</div></div>
                      <div className="bg-secondary/40 rounded p-1.5"><div className="text-[9px] uppercase text-muted-foreground">Recebido</div><div className="font-mono font-bold text-emerald-300">{recebido}</div></div>
                      <div className="bg-secondary/40 rounded p-1.5"><div className="text-[9px] uppercase text-muted-foreground">Faltante</div><div className={`font-mono font-bold ${faltante > 0 ? "text-red-300" : "text-muted-foreground"}`}>{faltante}</div></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Ocorrências registradas neste recebimento */}
          {(sel.ocorrencias || []).filter((o) => o.foto_url || o.subtipo).length > 0 && (
            <Card className="bg-card/70 p-4">
              <div className="font-display text-lg font-bold uppercase mb-2">Ocorrências Registradas</div>
              <div className="space-y-2">
                {(sel.ocorrencias || []).filter((o) => o.subtipo).map((o) => (
                  <div key={o.id} className="text-sm flex items-center justify-between border-b border-border pb-1.5">
                    <span><b className="text-rose-300">{o.subtipo_label || o.tipo}</b> · {o.sku_nome || "—"} · qtd {o.quantidade}</span>
                    {o.foto_path && <span className="text-[10px] text-emerald-300 font-mono">FOTO ✓</span>}
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Button className="w-full h-14 text-lg font-bold" onClick={confirmar} disabled={busy} data-testid="btn-confirmar-recebimento">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar Recebimento"}
          </Button>
        </div>
      )}

      {/* Modal Divergência / Avaria */}
      <Dialog open={divModal} onOpenChange={setDivModal}>
        <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase">Registrar Divergência / Avaria</DialogTitle>
            <DialogDescription>Vincule a ocorrência a um item. Avaria exige foto capturada ao vivo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Item</Label>
              <Select value={div.item_id} onValueChange={(v) => setD("item_id", v)}>
                <SelectTrigger data-testid="div-item-select"><SelectValue placeholder="Item" /></SelectTrigger>
                <SelectContent>{sel?.itens.map((it) => <SelectItem key={it.id} value={it.id}>{it.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <Select value={div.tipo} onValueChange={(v) => { setD("tipo", v); if (v !== "AVARIA") setD("foto_path", null); }}>
                  <SelectTrigger data-testid="div-tipo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{DIVERG_TIPOS.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Quantidade</Label><Input type="number" min={0} value={div.quantidade} onChange={(e) => setD("quantidade", e.target.value)} data-testid="div-qtd" /></div>
            </div>
            <div><Label>Motivo / Observação</Label><Textarea value={div.motivo} onChange={(e) => setD("motivo", e.target.value)} data-testid="div-motivo" /></div>

            {div.tipo === "AVARIA" && (
              <>
                <CameraCapture onCaptured={(path) => setD("foto_path", path)} />
                <div className="rounded-md border border-rose-600/40 bg-rose-950/20 p-3 space-y-2">
                  <Label className="text-rose-200 text-xs uppercase tracking-wide">Destino da quantidade avariada</Label>
                  {[["RECEBER_COM_AVARIA", "Receber com avaria", "Entra no estoque BLOQUEADO da unidade (sem disponibilidade)."],
                    ["RECUSAR_DEVOLVER", "Recusar e devolver ao CD", "Não entra no estoque. Gera devolução vinculada ao pedido."],
                    ["AGUARDAR_DECISAO", "Aguardar decisão", "Vai para QUARENTENA até decisão de usuário autorizado."]].map(([v, l, h]) => (
                    <label key={v} className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer ${div.destino === v ? "border-rose-400 bg-rose-500/10" : "border-border"}`} data-testid={`avaria-destino-${v}`}>
                      <input type="radio" name="avaria-destino" checked={div.destino === v} onChange={() => setD("destino", v)} className="mt-1 accent-rose-400" />
                      <span><span className="text-sm font-semibold block">{l}</span><span className="text-[11px] text-muted-foreground">{h}</span></span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDivModal(false)}>Cancelar</Button>
            <Button onClick={salvarDiv} disabled={busy || (div.tipo === "AVARIA" && (!div.foto_path || !div.destino))} data-testid="btn-salvar-divergencia">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registrar Ocorrência"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
